import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import {
  ImageGrid,
  ImagesBox,
  TextAreaBox,
  TextPlaceholder,
} from '../VariationTwo';
import { Filler } from '../shared';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';

export const DIVIDERS = ['white', 'black'];

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightText: string;
  divider: string;
  brakes: boolean;
  template: 'poll' | 'you' | 'type' | 'trivia';
  fontSize?: number;
}

const StyledUploadedImages = styled(UploadedImages)`
  height: 382px;
`;

const StyledTextArea = styled(Textarea)`
  height: 168px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 358px;
`;

const Selection = styled.label`
  display: flex;
  border-radius: 4px;
  height: 24px;
  align-items: center;
  flex: 1;
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  gap: 9px;
  white-space: nowrap;
`;

const AdditionalBox = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Radio = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  position: relative;
  transition: border-color ease-out 0.2s;
`;

const RadioCircle = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  position: absolute;
  margin: auto;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  transition: background ease-out 0.2s;
`;

const RadioInput = styled.input`
  &:checked {
    + ${Selection} {
      border-color: ${({ theme }) => theme.colors.purple};
      ${Radio} {
        border-color: ${({ theme }) => theme.colors.purple};
      }
      ${RadioCircle} {
        background: ${({ theme }) => theme.colors.purple};
      }
    }
  }
  &:disabled {
    + ${Selection} {
      border-color: ${({ theme }) => theme.colors.grayscale};
      cursor: not-allowed;
      ${Radio} {
        border-color: ${({ theme }) => theme.colors.gray};
      }
      ${RadioCircle} {
        background: transparent;
      }
      ${Text} {
        color: ${({ theme }) => theme.colors.gray};
      }
    }
  }
  &:checked&:disabled {
    + ${Selection} {
      ${RadioCircle} {
        background: ${({ theme }) => theme.colors.gray};
      }
    }
  }
`;

interface Props {
  inArticle?: boolean;
}

const VariationNeonTrivia: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [brakes, setBrakes] = useState(false);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [fontSize, setFontSize] = useState('');
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();
  const [template, setTemplate] = useState<'poll' | 'you' | 'type' | 'trivia'>(
    'poll',
  );

  const onReset = () => {
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setFontSize('');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      rightText: text,
      brakes: brakes,
      divider: leftImage.divider,
      template: template,
      fontSize: parseInt(fontSize),
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const isResetDisabled = !leftImages?.length && !text && !brakes && !fontSize;

  return (
    <>
      <Filler fontFamily="GothicBold" fontWeight={700} />
      <Box>
        <Header>
          <div>
            <Heading $type="button">Neon Trivia</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one image on the left side and add text on the right with
              the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <ImageGrid>
            <ImagesBox $hasImages={!!leftImages?.length}>
              <LabelText $type="smallHeadline">Left Images</LabelText>
              {!!leftImages?.length ? (
                <>
                  <StyledUploadedImages>
                    {renderLeftImages}
                  </StyledUploadedImages>
                  <MoreImages>
                    <AttachedCountBadge count={leftImages.length} />
                    <AddMoreButton
                      onClick={() => setShowMoreImagesModal(true)}
                      color="colorized"
                    >
                      Add more images
                    </AddMoreButton>
                  </MoreImages>
                </>
              ) : (
                <StyledUploadMultipleImages
                  coverIndex={2}
                  setImages={setLeftImages}
                />
              )}
            </ImagesBox>
            <div>
              <TextAreaBox>
                <div>
                  <LabelText $type="smallHeadline">Right Text</LabelText>
                  <StyledTextArea
                    onChange={(e) => setText(e.target.value)}
                    value={text}
                    placeholder="Add text"
                  ></StyledTextArea>
                </div>
                <div>
                  <LabelText $type="smallHeadline">Font Size</LabelText>
                  <Input
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    placeholder="Font Size (px)"
                    type="number"
                    min="0"
                  />
                </div>
                <div>
                  <ActionTitle $type="smallHeadline">
                    Text Parameters
                  </ActionTitle>
                  <ActionBox>
                    <Checkbox
                      checked={brakes}
                      onChange={() => setBrakes((prev) => !prev)}
                    >
                      Custom brakes
                    </Checkbox>
                  </ActionBox>
                </div>
              </TextAreaBox>
              {!!leftImages?.length && <TextPlaceholder />}
            </div>
          </ImageGrid>
        )}
        <AdditionalBox>
          <Text $type="smallHeadline" $color="gray">
            Additional:
          </Text>
          <RadioGroup>
            <RadioInput
              checked={template === 'poll'}
              name="template"
              type="radio"
              id="poll-template"
              hidden
              onChange={() => setTemplate('poll')}
            />
            <Selection htmlFor="poll-template">
              <Radio>
                <RadioCircle />
              </Radio>
              <Text $type="smallBold">Poll</Text>
            </Selection>
            <RadioInput
              checked={template === 'you'}
              name="template"
              type="radio"
              id="you-template"
              hidden
              onChange={() => setTemplate('you')}
            />
            <Selection htmlFor="you-template">
              <Radio>
                <RadioCircle />
              </Radio>
              <Text $type="smallBold">You 101</Text>
            </Selection>
            <RadioInput
              checked={template === 'type'}
              name="template"
              type="radio"
              id="type-template"
              hidden
              onChange={() => setTemplate('type')}
            />
            <Selection htmlFor="type-template">
              <Radio>
                <RadioCircle />
              </Radio>
              <Text $type="smallBold">Type it</Text>
            </Selection>
            <RadioInput
              checked={template === 'trivia'}
              name="template"
              type="radio"
              id="trivia-template"
              hidden
              onChange={() => setTemplate('trivia')}
            />
            <Selection htmlFor="trivia-template">
              <Radio>
                <RadioCircle />
              </Radio>
              <Text $type="smallBold">Trivia</Text>
            </Selection>
          </RadioGroup>
        </AdditionalBox>
      </Box>
      {showContent && (
        <ButtonHolder>
          <Button disabled={!leftImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationNeonTrivia;
